/**
 * The report a purchase is meant to unlock, carried from the preview PDF's
 * checkout link through Stripe and back to /payment-success.
 *
 * Stripe returns the buyer to the same tab, so sessionStorage survives the round
 * trip while keeping the order id out of the return URL. It is a hint, not an
 * authorization: unlock-report still verifies ownership and charges a credit.
 */
const STORAGE_KEY = "nreport_unlock_order";

export function setUnlockTarget(orderId: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, orderId);
  } catch {
    // Private-mode storage failures just cost the auto-unlock; the report page
    // still offers the unlock button once the credit lands.
  }
}

export function getUnlockTarget(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearUnlockTarget(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

type UnlockCheckoutOrder = {
  is_preview: boolean | null;
  unlocked_at: string | null;
  unlock_requested_at: string | null;
};

/**
 * Whether checkout for this unlock should send the buyer to the report instead
 * of asking for payment: the report is already full, its unlock is already paid
 * and held for the owner's release (unlock_requested_at, TASK-121/147), or the
 * buyer holds a paid credit the report page can spend.
 */
export function shouldSkipUnlockCheckout(order: UnlockCheckoutOrder, paidCredits: number | null): boolean {
  if (!order.is_preview || order.unlocked_at) return true;
  if (order.unlock_requested_at) return true;
  return (paidCredits ?? 0) >= 1;
}
